import { TimeCalculator } from "./TimeCalculator";
import { getMatchStartedAt, getCommencedAt } from "../utils/match-helper";
import { isNumeric } from "../utils/number-helper";
export class MoveStatsCalculator {
    match;
    moves;
    timeCalculator;
    constructor(match, moves, firestoreTimeOffset = 0) {
        this.match = match;
        this.moves = moves;
        this.timeCalculator = new TimeCalculator(match, moves, firestoreTimeOffset);
    }
    /**
     * Calculate comprehensive move statistics
     */
    calculateMovesStats() {
        if (!this.isValidForCalculation()) {
            return [];
        }
        const context = this.createCalculationContext();
        return this.moves.map((move, index) => this.calculateMoveStats(move, index, context));
    }
    // Private helper methods
    isValidForCalculation() {
        if (!this.match)
            return false;
        if (!this.moves || !this.moves.length)
            return false;
        const lastMove = this.moves.at(-1);
        // Valid if we have either createdAt or receivedAt as fallback timestamp
        if (!lastMove?.createdAt && !lastMove?.receivedAt)
            return false;
        return true;
    }
    createCalculationContext() {
        const matchStartedAt = getMatchStartedAt(this.match, this.moves);
        const matchCommencedAt = getCommencedAt(this.match, this.moves);
        const startTimerIndex = this.match.tournamentId ? 0 : 2;
        const blackToStart = this.getBlackToStart();
        return {
            matchStartedAt,
            matchCommencedAt,
            startTimerIndex,
            blackToStart,
            playersTimeUsed: [0, 0],
            playersBufferUsed: [0, 0],
            playersTimeLeft: [this.match.duration, this.match.duration],
            playersBufferLeft: [
                this.match.buffer_duration || 0,
                this.match.buffer_duration || 0,
            ],
        };
    }
    calculateMoveStats(move, index, context) {
        const previousMoveTimestamp = index
            ? this.timeCalculator.calculateMoveTimestamp(this.moves[index - 1], move)
            : context.matchStartedAt;
        const moveTimestamp = this.timeCalculator.calculateMoveTimestamp(move, this.moves[index + 1] ?? null);
        // Create base move stats
        const moveStats = {
            ...move,
            moveIndex: index,
            fullMoveNumber: (index + context.blackToStart) / 2,
            timeOffset: this.calculateTimeOffset(move),
            e2eTime: this.calculateE2ETime(moveTimestamp, previousMoveTimestamp),
            sendTime: this.timeCalculator.calculateMoveSendTime(move),
            moveTimestamp,
            bufferTime: 0,
            actualMoveTime: 0,
            moveTime: 0,
        };
        // Set color if not already set
        if (!moveStats.color) {
            moveStats.color = (index + context.blackToStart) % 2 ? "black" : "white";
        }
        // Calculate buffer time
        this.calculateBufferTime(moveStats, index, context);
        // Calculate actual move time
        moveStats.actualMoveTime =
            index > 0
                ? moveStats.e2eTime -
                    this.timeCalculator.calculateMoveSendTime(this.moves[index - 1])
                : moveStats.e2eTime;
        // Calculate move time
        this.calculateMoveTime(moveStats, index, context, moveTimestamp);
        // Update time tracking
        this.updateTimeTracking(moveStats, index, context);
        // Handle increments
        this.handleIncrements(moveStats, index, context);
        // Set final time values
        moveStats.timeUsed = context.playersTimeUsed[index % 2];
        moveStats.timeLeft = context.playersTimeLeft[index % 2];
        return moveStats;
    }
    calculateTimeOffset(move) {
        return move.localTimestamp
            ? this.getTimestampDifference(move.createdAt, move.localTimestamp)
            : 0;
    }
    calculateE2ETime(moveTimestamp, previousMoveTimestamp) {
        return moveTimestamp &&
            previousMoveTimestamp &&
            this.isTimestampLater(moveTimestamp, previousMoveTimestamp)
            ? this.getTimestampDifference(moveTimestamp, previousMoveTimestamp)
            : 0;
    }
    calculateBufferTime(moveStats, index, context) {
        const lastMoveSendTime = this.timeCalculator.calculateMoveSendTime(this.moves[index - 1]);
        if (context.playersBufferLeft[index % 2] > 0 &&
            index >= context.startTimerIndex) {
            moveStats.bufferTime = Math.min(lastMoveSendTime, context.playersBufferLeft[index % 2]);
            context.playersBufferLeft[index % 2] -= moveStats.bufferTime;
            context.playersBufferUsed[index % 2] += moveStats.bufferTime;
        }
        else {
            moveStats.bufferTime = 0;
        }
        moveStats.bufferUsed = context.playersBufferUsed[index % 2];
        moveStats.bufferLeft = context.playersBufferLeft[index % 2];
    }
    calculateMoveTime(moveStats, index, context, moveTimestamp) {
        if (index >= context.startTimerIndex &&
            this.isTimestampLater(moveTimestamp, context.matchCommencedAt)) {
            if (index > 0 || context.startTimerIndex > 0) {
                moveStats.moveTime = moveStats.e2eTime - moveStats.bufferTime;
            }
            else {
                moveStats.moveTime = this.getTimestampDifference(moveTimestamp, context.matchCommencedAt);
            }
        }
        else {
            moveStats.moveTime = 0;
        }
    }
    updateTimeTracking(moveStats, index, context) {
        context.playersTimeUsed[index % 2] += moveStats.moveTime;
        context.playersTimeLeft[index % 2] -= moveStats.moveTime;
    }
    handleIncrements(moveStats, index, context) {
        if (index >= context.startTimerIndex && isNumeric(this.match.increment)) {
            if (moveStats.received || index < this.moves.length - 1) {
                moveStats.increment = this.match.increment;
                context.playersTimeLeft[index % 2] += this.match.increment;
            }
        }
    }
    getBlackToStart() {
        return this.match.initial_fen && this.match.initial_fen.includes(" b ")
            ? 1
            : 0;
    }
    isTimestampLater(timestamp1, timestamp2) {
        if (timestamp1?.toMillis && timestamp2?.toMillis) {
            return timestamp1.toMillis() > timestamp2.toMillis();
        }
        return timestamp1 > timestamp2;
    }
    getTimestampDifference(timestamp1, timestamp2) {
        // Handle Firebase Timestamp subtraction - convert to seconds
        if (timestamp1?.toMillis && timestamp2?.toMillis) {
            return (timestamp1.toMillis() - timestamp2.toMillis()) / 1000;
        }
        // Fallback for other timestamp types - convert to seconds
        return (Number(timestamp1) - Number(timestamp2)) / 1000;
    }
}
